import { toast } from 'react-toastify';
import store from 'redux/store';
import { showDialog, waitDialog } from 'dialogs';
import type { CreateTarget } from 'actions';
import { getTable } from 'api/tables/endpoints/getTable';
import EditTableItem from '../editTableItem';
import EditTableRows from '../editTableRows';

export type ShowEditTableArgs = { readonly mode: 'create'; readonly createIn?: CreateTarget } | { readonly mode: 'edit'; readonly id: number };

// Resolve the zone/name identity to a store-list index (-1 → null), and the
// record itself when present.
const findTableIndex = (zoneName: string, tableName: string): number | null => {
  const index = store.getState().tables.items.findIndex((t: any) => t.zone_name === zoneName && t.table_name === tableName);
  return index >= 0 ? index : null;
};

/**
 * Open the table create / edit modal and resolve when it closes (so the caller
 * can refresh). For edit it ensures the record is available — from the slice
 * (by index), else fetched by zone + name — so it works from the Workspace,
 * which never populates that slice.
 */
export const showEditTable = async (args: ShowEditTableArgs): Promise<void> => {
  if (args.mode === 'create') {
    await waitDialog<void>((ok) => <EditTableItem tableIndex={null} isNew handleClose={ok} createIn={args.createIn} />);
    return;
  }
  // Always fetch the record by id (boundary-validated) so the editor opens on
  // fresh, validated data — never a loosely-cast store row — and works from the
  // Workspace, which never populates the list slice.
  const record = await getTable(args.id);
  if (!record) {
    toast.error('Table not found; the list may be out of date. Please refresh.');
    return;
  }
  await waitDialog<void>((ok) => <EditTableItem tableIndex={null} record={record} isNew={false} handleClose={ok} />);
};

/** Open the table rows editor (EditTableRows) via showDialog. */
export const showEditTableRows = (zoneName: string, tableName: string): void => {
  const tableIndex = findTableIndex(zoneName, tableName);
  showDialog((close) => <EditTableRows tableIndex={tableIndex} handleClose={close} />);
};
